import React, { FC, useCallback, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { FlatList, LayoutChangeEvent } from 'react-native';

import Alert from 'components/alert';
import { AppDownloadSection } from 'components/features/components/app-download-section';
import { MarketingTileGroup } from 'components/features/components/marketing-tile-group';
import { RecentItemsSection } from 'components/features/components/recent-items';
import { RewardsSection } from 'components/features/components/rewards-section';
import { SimpleHomePage } from 'components/features/components/simple-home-page';
import { Footer } from 'components/footer';
import { LayoutContainer } from 'components/layout/styled';
import LoadingAnimation from 'components/loading-animation';
import { QuestSection } from 'components/quests';
import { IMarketingCardGroupFragment, IMarketingTileGroup } from 'generated/sanity-graphql';
import { useIsDesktopLargeBreakpoint, useIsMobileBreakpoint } from 'hooks/use-media-query';
import { isNative } from 'utils/environment';

import { Section, Spacer } from './home-page/styles';
import { Component, Components } from './home-page/types';

const itemIdExtractor = (item: Component | null, index: number) => item?._id || index + '_item';

const Spinner = () => (
  <LayoutContainer>
    <Box padding="$2">
      <LoadingAnimation />
    </Box>
  </LayoutContainer>
);

// A hack to save the calculated height value of the FlatList to every render. Since when user navigate to another page, the component is unmounted and we lose the height.
let scrollHeight = 0;

export const Features: FC<React.PropsWithChildren<{
  components: Components;
  headerComponent?: React.ReactElement | null;
}>> = React.memo(({ headerComponent, components }) => {
  const [flatListHeight, setFlatListHeight] = useState(scrollHeight);
  const componentLength = components?.length || 0;
  const isMobile = useIsMobileBreakpoint();
  const isDesktopLarge = useIsDesktopLargeBreakpoint();

  const renderItem = useCallback(
    ({ item, index }: { item: Component | null; index: number }) => {
      if (!item) {
        return null;
      }

      const first = index === 0;
      const last = index + 1 === componentLength;
      switch (item.__typename) {
        case 'Spacer': {
          return <Spacer $isSmall={item.isSmall} />;
        }

        case 'SimpleHomePage': {
          return <SimpleHomePage item={item} height={flatListHeight} />;
        }

        case 'Alert': {
          return (
            <Alert
              headline={item.heading?.locale || ''}
              body={item.body?.locale || ''}
              learnMore={item.learnMoreText?.locale || ''}
              learnMoreUrl={item.learnMoreUrl?.locale || ''}
            />
          );
        }

        case 'MarketingCardGroup':
          return (
            <LayoutContainer>
              <Section first={first} last={last} shouldRenderDefaultBackground={true}>
                <MarketingTileGroup item={(item as unknown) as IMarketingCardGroupFragment} />
              </Section>
            </LayoutContainer>
          );

        case 'MarketingTileGroup':
          return (
            <LayoutContainer>
              <Section first={first} last={last}>
                <MarketingTileGroup item={item as IMarketingTileGroup} />
              </Section>
            </LayoutContainer>
          );

        case 'RecentItems': {
          return (
            <LayoutContainer isFullContainer={isMobile}>
              <RecentItemsSection />
            </LayoutContainer>
          );
        }

        case 'RewardsSection': {
          return (
            <LayoutContainer isFullContainer={isMobile}>
              <RewardsSection item={item} />
            </LayoutContainer>
          );
        }

        case 'QuestsSection': {
          return (
            <LayoutContainer>
              <QuestSection />
            </LayoutContainer>
          );
        }

        case 'AppDownload': {
          return <AppDownloadSection item={item.details} />;
        }

        case 'Footer': {
          return <Footer />;
        }

        default: {
          return null;
        }
      }
    },
    [componentLength, flatListHeight, isMobile]
  );

  const [isLoadingMore, setIsLoadingMore] = useState(true);
  const setIsLoadingMoreFalse = useCallback(() => setIsLoadingMore(false), []);

  // Here we need to get the height of the flatList to pass it to the SimpleHomePage component an let it fit the screen
  const onLayout = useCallback(({ nativeEvent }: LayoutChangeEvent) => {
    const { height: newHeight } = nativeEvent.layout;
    setFlatListHeight(oldHeight => {
      // measuring screen height onLayout and putting content in its measured height is inherently hacky in react native
      // (but desired by design) and because of this there seems to be some weird onLayout jitter occurring as the app loads that is causing
      // simple home page image to load multiple times and load/unload/load flashing (especially with animated gifs on iOS)
      // this eliminates the problem
      if (Math.abs(oldHeight - newHeight) <= 5) {
        return oldHeight;
      }

      // This hack prevents to set the height to 0 when the user navigate to another page.
      scrollHeight = newHeight;

      return newHeight;
    });
  }, []);

  if (!components) {
    return null;
  }

  return (
    <FlatList
      ListHeaderComponent={headerComponent}
      ListFooterComponent={isLoadingMore ? Spinner : null}
      keyExtractor={itemIdExtractor}
      data={components}
      initialNumToRender={1}
      renderItem={renderItem}
      onEndReached={setIsLoadingMoreFalse}
      // Changed from 0 to 0.01 because with 0 onEndReached was not being called on web
      // 0.01 is a small value, close to the original, that works on both web and mobile
      onEndReachedThreshold={0.01}
      {...(isNative && {
        windowSize: 1,
      })}
      onLayout={onLayout}
      showsVerticalScrollIndicator={isDesktopLarge}
    />
  );
});
